import { useState, useEffect, useCallback } from "react";
import {
  Clock,
  Play,
  Loader2,
  CheckCircle2,
  XCircle,
  Trash2,
  HardDrive,
  AlertTriangle,
} from "lucide-react";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";

interface CronJob {
  name: string;
  schedule: string;
  description: string;
}

interface CronRun {
  id: number;
  job_name: string;
  started_at: number;
  finished_at: number | null;
  status: string;
  result: string | null;
  items_deleted: number;
  items_checked: number;
}

interface R2Stats {
  total_objects: number;
  referenced: number;
  orphaned: number;
}

function formatSchedule(cron: string): string {
  if (cron === "0 3 * * *") return "Daily at 3:00 AM UTC";
  if (cron === "0 * * * *") return "Every hour";
  return cron;
}

function timeAgo(ms: number): string {
  const diff = Date.now() - ms;
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function formatDate(ms: number): string {
  return new Date(ms).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}

function durationStr(start: number, end: number | null): string {
  if (!end) return "—";
  const ms = end - start;
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

export function AdminMaintenance() {
  const [jobs, setJobs] = useState<CronJob[]>([]);
  const [runs, setRuns] = useState<CronRun[]>([]);
  const [r2Stats, setR2Stats] = useState<R2Stats | null>(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const data = await api.admin.maintenance.status();
      setJobs(data.jobs);
      setRuns(data.runs);
      setR2Stats(data.r2_stats);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const triggerJob = async (jobName: string) => {
    setRunning(jobName);
    try {
      await api.admin.maintenance.runJob(jobName);
      await load();
    } finally {
      setRunning(null);
    }
  };

  const triggerAll = async () => {
    setRunning("all");
    try {
      await api.admin.maintenance.runAll();
      await load();
    } finally {
      setRunning(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 pt-12 text-fog">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading…
      </div>
    );
  }

  const lastRun = runs.find((r) => r.job_name === "r2-orphan-cleanup");

  return (
    <div>
      <h1 className="font-display text-2xl font-bold tracking-display">
        Maintenance
      </h1>

      {/* Stats cards */}
      <div className="mt-6 grid gap-4 sm:grid-cols-3">
        <div className="rounded-[18px] border border-mist bg-slate p-4">
          <div className="flex items-center gap-2 text-fog">
            <HardDrive className="h-4 w-4" />
            <span className="font-mono text-[10px] uppercase tracking-wider">
              R2 Objects
            </span>
          </div>
          <p className="mt-2 text-2xl font-bold text-parchment">
            {r2Stats?.total_objects ?? "—"}
          </p>
          <p className="mt-0.5 font-mono text-[11px] text-fog">
            {r2Stats?.referenced ?? 0} referenced by sounds
          </p>
        </div>

        <div className="rounded-[18px] border border-mist bg-slate p-4">
          <div className="flex items-center gap-2 text-fog">
            <Trash2 className="h-4 w-4" />
            <span className="font-mono text-[10px] uppercase tracking-wider">
              Orphaned
            </span>
          </div>
          <p
            className={cn(
              "mt-2 text-2xl font-bold",
              r2Stats && r2Stats.orphaned > 0 ? "text-ember" : "text-moss",
            )}
          >
            {r2Stats?.orphaned ?? "—"}
          </p>
          <p className="mt-0.5 font-mono text-[11px] text-fog">
            unreferenced objects in R2
          </p>
        </div>

        <div className="rounded-[18px] border border-mist bg-slate p-4">
          <div className="flex items-center gap-2 text-fog">
            <Clock className="h-4 w-4" />
            <span className="font-mono text-[10px] uppercase tracking-wider">
              Last Run
            </span>
          </div>
          <p className="mt-2 text-2xl font-bold text-parchment">
            {lastRun ? timeAgo(lastRun.started_at) : "Never"}
          </p>
          <p className="mt-0.5 font-mono text-[11px] text-fog">
            {lastRun
              ? `${lastRun.items_deleted} deleted, ${lastRun.items_checked} checked`
              : "No runs recorded yet"}
          </p>
        </div>
      </div>

      {/* Cron jobs */}
      <div className="mt-8 flex items-center justify-between">
        <h2 className="font-display text-lg font-semibold tracking-display text-parchment">
          Scheduled Jobs
        </h2>
        <button
          onClick={triggerAll}
          disabled={running !== null}
          className="flex items-center gap-1.5 rounded-xl bg-spark px-3 py-1.5 text-sm font-medium text-ink hover:bg-spark-soft disabled:opacity-50"
        >
          {running === "all" ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Play className="h-3.5 w-3.5 fill-current" />
          )}
          {running === "all" ? "Running all…" : "Run all now"}
        </button>
      </div>

      <div className="mt-3 space-y-3">
        {jobs.map((job) => {
          const jobRuns = runs.filter((r) => r.job_name === job.name);
          const last = jobRuns[0];

          return (
            <div
              key={job.name}
              className="rounded-[18px] border border-mist bg-slate p-4"
            >
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="text-sm font-medium text-parchment">
                      {job.name}
                    </h3>
                    <span className="rounded-lg bg-spark/10 px-2 py-0.5 font-mono text-[10px] text-spark">
                      {formatSchedule(job.schedule)}
                    </span>
                    {last && (
                      <span
                        className={cn(
                          "flex items-center gap-1 rounded-lg px-2 py-0.5 font-mono text-[10px]",
                          last.status === "success"
                            ? "bg-moss/10 text-moss"
                            : last.status === "error"
                              ? "bg-ember/10 text-ember"
                              : "bg-spark/10 text-spark",
                        )}
                      >
                        {last.status === "success" ? (
                          <CheckCircle2 className="h-3 w-3" />
                        ) : last.status === "error" ? (
                          <XCircle className="h-3 w-3" />
                        ) : (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        )}
                        {last.status}
                      </span>
                    )}
                  </div>
                  <p className="mt-1 text-sm text-fog">{job.description}</p>
                </div>

                <button
                  onClick={() => triggerJob(job.name)}
                  disabled={running !== null}
                  className="flex shrink-0 items-center gap-1.5 rounded-xl border border-mist px-3 py-1.5 text-sm font-medium text-parchment transition-colors hover:border-spark/50 hover:bg-spark/5 disabled:opacity-50"
                >
                  {running === job.name ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Play className="h-3.5 w-3.5 fill-current" />
                  )}
                  {running === job.name ? "Running…" : "Run now"}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Run history */}
      <h2 className="mt-8 font-display text-lg font-semibold tracking-display text-parchment">
        Run History
      </h2>

      {runs.length === 0 ? (
        <div className="mt-4 flex items-center gap-2 rounded-[18px] border border-mist bg-slate p-6 text-fog">
          <AlertTriangle className="h-4 w-4" />
          <span className="text-sm">
            No cron runs recorded yet. The first run will happen at 3:00 AM UTC.
          </span>
        </div>
      ) : (
        <div className="mt-3 overflow-hidden rounded-[18px] border border-mist">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-mist bg-slate">
                <th className="px-4 py-2.5 text-left font-mono text-[10px] uppercase tracking-wider text-fog">
                  Job
                </th>
                <th className="px-4 py-2.5 text-left font-mono text-[10px] uppercase tracking-wider text-fog">
                  Started
                </th>
                <th className="px-4 py-2.5 text-left font-mono text-[10px] uppercase tracking-wider text-fog">
                  Duration
                </th>
                <th className="px-4 py-2.5 text-left font-mono text-[10px] uppercase tracking-wider text-fog">
                  Status
                </th>
                <th className="px-4 py-2.5 text-right font-mono text-[10px] uppercase tracking-wider text-fog">
                  Deleted
                </th>
                <th className="px-4 py-2.5 text-right font-mono text-[10px] uppercase tracking-wider text-fog">
                  Checked
                </th>
              </tr>
            </thead>
            <tbody>
              {runs.map((run) => (
                <tr
                  key={run.id}
                  className="border-b border-mist/50 last:border-0"
                >
                  <td className="px-4 py-2.5 font-mono text-xs text-parchment">
                    {run.job_name}
                  </td>
                  <td className="px-4 py-2.5 text-fog">
                    {formatDate(run.started_at)}
                  </td>
                  <td className="px-4 py-2.5 font-mono text-xs text-fog">
                    {durationStr(run.started_at, run.finished_at)}
                  </td>
                  <td className="px-4 py-2.5">
                    <span
                      className={cn(
                        "inline-flex items-center gap-1 rounded-lg px-2 py-0.5 font-mono text-[10px]",
                        run.status === "success"
                          ? "bg-moss/10 text-moss"
                          : run.status === "error"
                            ? "bg-ember/10 text-ember"
                            : "bg-spark/10 text-spark",
                      )}
                    >
                      {run.status === "success" ? (
                        <CheckCircle2 className="h-3 w-3" />
                      ) : run.status === "error" ? (
                        <XCircle className="h-3 w-3" />
                      ) : (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      )}
                      {run.status}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-right font-mono text-xs text-parchment">
                    {run.items_deleted}
                  </td>
                  <td className="px-4 py-2.5 text-right font-mono text-xs text-fog">
                    {run.items_checked}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
